import { t } from "@lingui/macro";
import {
  ISocialPageGroupSetQuery,
  ISocialPageGroupSetQueryVariables,
  useSocialPageGroupSetQuery,
} from "@smp/graphql";
import produce from "immer";
import { WritableDraft } from "immer/dist/types/types-external";
import { useCallback, useMemo } from "react";
import { useQueryClient, UseQueryOptions } from "react-query";

import { gqlClient } from "@/hooks/use_graphql";
import { useQueryGetPageConfigSetAll } from "@/queries/use_query_get_page_config_set_all";
import { useContextApp } from "@/screens/app/context/app";

interface Params extends ISocialPageGroupSetQueryVariables {
  include_out_of_group_page_configs?: boolean;
}

export const useQuerySocialPageGroupSet = (
  params?: Params,
  options?: Omit<
    UseQueryOptions<ISocialPageGroupSetQuery>,
    "queryFn" | "queryKey" | "getNextPageParam" | "refetchOnMount"
  > & {
    refetchOnMount?: boolean | "always";
  },
) => {
  const { include_out_of_group_page_configs, ...variables } = params || {};

  const {
    state: { organizationId },
  } = useContextApp();

  const isOutOfGroupsRequired =
    organizationId === null && include_out_of_group_page_configs;

  const queryPageConfigSet = useQueryGetPageConfigSetAll(
    {
      group: null,
    },
    {
      refetchOnMount: options?.refetchOnMount,
      enabled: isOutOfGroupsRequired,
    },
  );

  const queryPageGroupSet = useSocialPageGroupSetQuery(
    gqlClient,
    variables,
    options,
  );

  const unassignedGroup = useMemo(() => {
    let group = queryPageConfigSet.data.grouped?.find(
      (group) => group.id === 0,
    );

    if (!group && organizationId === null) {
      group = {
        id: 0,
        name: t`Unassigned`,
        page_configs: [],
      };
    }

    return group;
  }, [organizationId, queryPageConfigSet.data.grouped]);

  const dataCombined = useMemo(() => {
    const data =
      !isOutOfGroupsRequired || !unassignedGroup
        ? queryPageGroupSet.data
        : produce(queryPageGroupSet.data, (draft) => {
            draft?.social_page_group_set.unshift({
              pageconfig_set: unassignedGroup.page_configs,
              name: unassignedGroup.name,
              id: unassignedGroup.id,
              is_locked_for_update: false,
            });
          });

    return {
      ...data,
      social_page_group_set: (data?.social_page_group_set || []).filter(
        (group) => {
          const groupOrganizationId = group.organization?.id || null;

          return groupOrganizationId === organizationId;
        },
      ),
    };
  }, [
    isOutOfGroupsRequired,
    organizationId,
    queryPageGroupSet.data,
    unassignedGroup,
  ]);

  return {
    ...queryPageGroupSet,
    data: dataCombined,
    isLoading: queryPageGroupSet.isLoading || queryPageConfigSet.isLoading,
    isFetching: queryPageGroupSet.isFetching || queryPageConfigSet.isFetching,
    isFetched:
      !queryPageGroupSet.isFetching &&
      !queryPageConfigSet.isFetching &&
      queryPageGroupSet.isFetched &&
      (!isOutOfGroupsRequired || queryPageConfigSet.isFetched),
    isError: queryPageGroupSet.isError || queryPageConfigSet.isError,
  };
};

type UpdateCallback = (
  draft: WritableDraft<ISocialPageGroupSetQuery> | undefined,
) => void;

export const useQuerySocialPageGroupSetUpdater = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (callback: UpdateCallback) =>
      queryClient.setQueriesData<ISocialPageGroupSetQuery | undefined>(
        useSocialPageGroupSetQuery.getKey(),
        (data) => produce(data, callback),
      ),
    [queryClient],
  );
};

export const useQuerySocialPageGroupSetInvalidate = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (variables?: Parameters<typeof useSocialPageGroupSetQuery>[1]) =>
      queryClient.invalidateQueries(
        useSocialPageGroupSetQuery.getKey(variables),
      ),
    [queryClient],
  );
};
